/**
 * AuditReportsView
 * ─────────────────────────────────────────────────────────────────────────────
 * Vendor-facing reports & downloadable PDF statement. The "give this to your
 * bank" page.
 *
 * Tabs:
 *   - Summary  : KPI cards + daily activity bar chart + domain breakdown
 *   - Stock    : totals IN / OUT / WASTE + top movers
 *   - Cohorts  : monthly customer retention triangle
 *
 * Top-right: date-range picker + "Download PDF statement" button.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Card, Button, Form, Badge, Spinner, Alert, Tab, Tabs, Table,
} from "react-bootstrap";
import moment from "moment";
import {
  apiGetAuditSummary,
  apiGetAuditStockReport,
  apiGetAuditCohorts,
  apiGetAuditStatementPdf,
} from "../api/index";
import HelpTooltip from "../components/HelpTooltip";

const DOMAIN_COLOURS = {
  PRODUCT: "#2d7a5f",
  PRICING: "#b8860b",
  HOURS: "#385f9b",
  STAFF: "#8e44ad",
  MEDIA: "#16a085",
  PROFILE: "#6c757d",
  STOCK: "#27ae60",
  ORDER: "#2980b9",
  SESSION: "#34495e",
  CS_INTERVENTION: "#c0392b",
  FINANCIAL: "#8b5a2b",
};

function KpiCard({ title, value, icon, color = "#2d7a5f", help }) {
  return (
    <Card style={{ border: `2px solid ${color}22`, height: "100%" }}>
      <Card.Body className="py-3">
        <div className="text-muted small d-flex align-items-center">
          {icon && <i className={`fa ${icon} mr-2`} style={{ color }} />}
          {title}
          {help && <HelpTooltip text={help} />}
        </div>
        <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      </Card.Body>
    </Card>
  );
}

function BarChart({ data, xKey = "day", yKey = "count", height = 140 }) {
  if (!data || data.length === 0) {
    return <div className="text-muted small text-center py-3">No data in this period.</div>;
  }
  const max = Math.max(...data.map((d) => d[yKey] || 0), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height, borderBottom: "1px solid #eee", padding: "8px 4px" }}>
      {data.map((d) => {
        const pct = ((d[yKey] || 0) / max) * 100;
        return (
          <div
            key={d[xKey]}
            title={`${d[xKey]}: ${d[yKey]}`}
            style={{
              flex: 1,
              minWidth: 4,
              background: "#2d7a5f",
              height: `${pct}%`,
              borderRadius: 2,
              cursor: "help",
            }}
          />
        );
      })}
    </div>
  );
}

function DomainBreakdown({ byDomain }) {
  if (!byDomain || byDomain.length === 0) {
    return <div className="text-muted small">No activity in this period.</div>;
  }
  const total = byDomain.reduce((acc, d) => acc + d.count, 0);
  return (
    <div>
      {byDomain.map((d) => {
        const pct = Math.round((d.count / total) * 1000) / 10;
        const colour = DOMAIN_COLOURS[d.domain] || "#666";
        return (
          <div key={d.domain} style={{ marginBottom: 8 }}>
            <div className="d-flex justify-content-between" style={{ fontSize: 12 }}>
              <span><strong>{d.domain}</strong></span>
              <span>{d.count.toLocaleString()} ({pct}%)</span>
            </div>
            <div style={{ background: "#eee", height: 6, borderRadius: 3, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: colour }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AuditReportsView() {
  const [period, setPeriod] = useState({
    from: moment().subtract(30, "days").format("YYYY-MM-DD"),
    to: moment().format("YYYY-MM-DD"),
  });

  const [summary, setSummary] = useState(null);
  const [stock, setStock] = useState(null);
  const [cohorts, setCohorts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { from: period.from, to: period.to };
      const [s, st, co] = await Promise.all([
        apiGetAuditSummary(params),
        apiGetAuditStockReport(params),
        apiGetAuditCohorts({ ...params, months: 6 }),
      ]);
      setSummary(s?.data?.data || null);
      setStock(st?.data?.data || null);
      setCohorts(co?.data?.data || null);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    setDownloading(true);
    setError(null);
    try {
      const res = await apiGetAuditStatementPdf({ from: period.from, to: period.to });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lusuku-statement-${period.from}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to download statement");
    } finally {
      setDownloading(false);
    }
  };

  // Build the cohort table columns (unique sorted months)
  const cohortMonths = useMemo(() => {
    if (!cohorts?.cohorts) return [];
    const set = new Set();
    for (const c of cohorts.cohorts) c.retention.forEach((r) => set.add(r.month));
    return [...set].sort();
  }, [cohorts]);

  return (
    <Container fluid className="py-3">
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <div>
                  <Card.Title as="h4" className="mb-0">
                    <i className="fa fa-chart-line mr-2" /> Reports & Statements
                    <HelpTooltip text="Everything you need to prove your shop's performance to a bank, investor, or auditor. Every number here traces back to an immutable audit record. Download a signed PDF statement with one click." />
                  </Card.Title>
                  <div className="text-muted small mt-1">
                    Period: <strong>{period.from}</strong> → <strong>{period.to}</strong>
                  </div>
                </div>
                <div className="d-flex align-items-end" style={{ gap: 8 }}>
                  <div>
                    <Form.Label className="small mb-0">From</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      value={period.from}
                      onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Form.Label className="small mb-0">To</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      value={period.to}
                      onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
                    />
                  </div>
                  <Button variant="primary" size="sm" onClick={downloadPdf} disabled={downloading}>
                    {downloading ? <Spinner size="sm" animation="border" /> : <><i className="fa fa-download mr-1" /> PDF statement</>}
                  </Button>
                </div>
              </div>
            </Card.Header>

            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              {loading && <div className="text-center py-3"><Spinner size="sm" animation="border" /> Building reports…</div>}

              {!loading && (
                <Tabs defaultActiveKey="summary" className="mb-3">
                  <Tab eventKey="summary" title="Summary">
                    <Row className="g-3 mb-4">
                      <Col md={3}>
                        <KpiCard title="Total activities"
                          icon="fa-stream"
                          value={(summary?.totalActivity ?? 0).toLocaleString()}
                          help="Every auditable event on your shop in this period." />
                      </Col>
                      <Col md={3}>
                        <KpiCard title="People acting" color="#8e44ad"
                          icon="fa-users"
                          value={summary?.uniqueActorCount ?? 0}
                          help="Unique users (you or your staff) who made any change." />
                      </Col>
                      <Col md={3}>
                        <KpiCard title="Busiest domain" color="#385f9b"
                          icon="fa-bullseye"
                          value={summary?.byDomain?.[0]?.domain || "—"}
                          help="Which area of your shop saw the most change." />
                      </Col>
                      <Col md={3}>
                        <KpiCard title="Top action" color="#b8860b"
                          icon="fa-star"
                          value={summary?.topActions?.[0]?.action || "—"}
                          help="The single most-used action in this period." />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={8}>
                        <h6>Daily activity</h6>
                        <BarChart data={summary?.daily || []} />
                      </Col>
                      <Col md={4}>
                        <h6>By domain</h6>
                        <DomainBreakdown byDomain={summary?.byDomain || []} />
                      </Col>
                    </Row>
                  </Tab>

                  <Tab eventKey="stock" title="Stock">
                    <Row className="g-3 mb-4">
                      <Col md={3}><KpiCard title="Stock received" color="#27ae60" icon="fa-arrow-down"
                        value={(stock?.totals?.in ?? 0).toLocaleString()} /></Col>
                      <Col md={3}><KpiCard title="Stock sold" color="#2980b9" icon="fa-arrow-up"
                        value={(stock?.totals?.out ?? 0).toLocaleString()} /></Col>
                      <Col md={3}><KpiCard title="Wasted" color="#c0392b" icon="fa-trash"
                        value={(stock?.totals?.waste ?? 0).toLocaleString()} /></Col>
                      <Col md={3}><KpiCard title="Manual corrections" color="#b8860b" icon="fa-balance-scale"
                        value={(stock?.totals?.adjust ?? 0).toLocaleString()} /></Col>
                    </Row>

                    <h6>Top movers</h6>
                    {(stock?.topMovers || []).length === 0 ? (
                      <div className="text-muted small">No movements in this period.</div>
                    ) : (
                      <Table size="sm" hover responsive style={{ fontSize: 13 }}>
                        <thead style={{ background: "#f7f7f9" }}>
                          <tr>
                            <th>Product</th>
                            <th>Type</th>
                            <th className="text-end">Sold</th>
                            <th className="text-end">Received</th>
                            <th className="text-end">Movements</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stock.topMovers.map((m) => (
                            <tr key={String(m.productId)}>
                              <td style={{ fontFamily: "monospace", fontSize: 11 }}>{String(m.productId).slice(-10)}</td>
                              <td><Badge bg="light" text="dark">{m.productType}</Badge></td>
                              <td className="text-end" style={{ color: "#c0392b", fontWeight: 600 }}>{Math.abs(m.totalOut).toLocaleString()}</td>
                              <td className="text-end" style={{ color: "#27ae60", fontWeight: 600 }}>{(m.totalIn || 0).toLocaleString()}</td>
                              <td className="text-end">{m.movementCount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Tab>

                  <Tab eventKey="cohorts" title="Customers">
                    {(cohorts?.cohorts || []).length === 0 ? (
                      <Alert variant="light">
                        <div className="text-center">
                          <div style={{ fontSize: 32 }}>📈</div>
                          <strong>No cohort data yet</strong>
                          <div className="text-muted small mt-1">
                            Once you have customers ordering across multiple months, this table will
                            show how many come back.
                          </div>
                        </div>
                      </Alert>
                    ) : (
                      <>
                        <p className="text-muted small">
                          Each row is a group of customers whose first order fell in that month. The
                          percentages show how many came back in each subsequent month.
                        </p>
                        <Table size="sm" bordered responsive style={{ fontSize: 12 }}>
                          <thead style={{ background: "#f7f7f9" }}>
                            <tr>
                              <th>Cohort</th>
                              <th>Size</th>
                              {cohortMonths.map((m) => <th key={m}>{m}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {cohorts.cohorts.map((c) => (
                              <tr key={c.cohort}>
                                <td><strong>{c.cohort}</strong></td>
                                <td>{c.size}</td>
                                {cohortMonths.map((m) => {
                                  const r = c.retention.find((x) => x.month === m);
                                  if (!r) return <td key={m} style={{ background: "#fafafa" }} />;
                                  const pct = r.retentionPct;
                                  const bg = pct >= 50 ? "#d4edda" : pct >= 25 ? "#fff3cd" : "#f8d7da";
                                  return (
                                    <td key={m} style={{ background: bg, textAlign: "center" }}>
                                      <div><strong>{pct}%</strong></div>
                                      <div style={{ fontSize: 10, color: "#666" }}>{r.activeCustomers}</div>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </Tab>
                </Tabs>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
